import createCli from "@pagedip/lib-cli";
import fs from "fs";
import log, { PrefixedLogger } from "@pagedip/util-logger";
import { Package, Env } from "./index";
import { matchBranch } from "./util";

const cli = createCli();
export = cli;

const { name, version } = JSON.parse(fs.readFileSync(__dirname + "/../package.json", "utf-8"));
cli.version(`${name} ${version}`);

cli.command({
  describe: `Automated Lerna package publishing`,
  strictValidation: false,
  flags: {
    dryrun: {
      type: "boolean",
      alias: ["n", "dry-run"],
      describe:
        `will calculate the release trajectory but will not actually release anything. ` +
        `this defaults to true when not in the CI environment`,
    },
    printChangelog: {
      type: "boolean",
      alias: "print-changelog",
      usageFlag: "print-changelog",
      describe: "print the changelog output to the console",
    },
    showAll: {
      type: "boolean",
      alias: ["show-all", "a", "all"],
      usageFlag: "show-all",
      describe: "Show all packages, including packages which won't be released",
    },
    expanded: {
      type: "boolean",
      describe: "Display release information in expanded format",
    },
    skipBuild: {
      type: "boolean",
      describe: "Add a '[skip ci]' to the commit message. Defaults to true. Pass --no-skip-build to disable.",
      alias: ["skip-build"],
      usageFlag: "skip-build",
    },
  },
  async handler({ app, argv: { flags } }) {
    const logger = log.prefix("autorelease");
    const options = {
      ...app.getOption("autorelease"),
      ...flags,
    };

    const env = await buildEnv(logger, options);
    displayCompleteStep(logger, "[1] verified environment and calculated release targets");

    const prepared = await env.prepare();
    displayCompleteStep(logger, "[2] saved changelogs, committed, tagged, and pushed", env.dryrun || !prepared);

    const published = prepared ? await env.publish() : [];
    displayCompleteStep(
      logger,
      `[3] published ${published.length} package${published.length !== 1 ? "s" : ""} to NPM`,
      env.dryrun || !prepared
    );
  },
});

async function buildEnv(logger: PrefixedLogger, options?: Env.ResolveOptions & DisplayPackageOptions) {
  logger.startProgress();
  logger.progress("verifying environment", 1 / 10);

  let idx = 0;
  const env = await Env.resolve({
    ...options,
    progress: (pkg, total) => {
      idx++;
      if (idx !== total) {
        logger.progress("calculating release targets", 1 / 10 + (idx / total) * (8 / 10));
        logger.pulse(pkg.name);
      } else {
        logger.progress("processing dependency tree", 9 / 10);
        logger.pulse();
      }
    },
  });

  logger.endProgress();
  displayEnv(logger, env, options);

  const warnings = new Map<string, Set<string>>();
  const errors = new Map<string, Set<string>>();

  for (const pkg of env.packages.values()) {
    displayPackage(logger, pkg, env, options);

    for (const warning of pkg.validations.warnings) {
      let w = warnings.get(warning);
      if (w == null) warnings.set(warning, (w = new Set()));
      w.add(pkg.name);
    }

    for (const error of pkg.validations.errors) {
      let e = errors.get(error);
      if (e == null) errors.set(error, (e = new Set()));
      e.add(pkg.name);
    }
  }

  displayProblems(logger, warnings, errors, Boolean(env.lerna));

  return env;
}

function displayCompleteStep(logger: PrefixedLogger, msg: string, skipped?: boolean) {
  const check = skipped ? "✘" : "✔";
  logger.info(log.color[skipped ? "gray" : "green"](`${msg} ${check}`));
}

function displayEnv(logger: PrefixedLogger, env: Env, options?: Env.ResolveOptions) {
  options = options || {};

  logger.info(log.color.bold(`Release #${env.id}`));

  if (env.lerna) {
    logger.info(log.color.magenta("This is a lerna project."));
  }

  if (env.dryrun) {
    logger.info("Dryrun mode. Nothing will be published. 👻");
  }

  logger.info(
    log.color.gray("CI:") + " %s",
    env.ci
      ? env.ci.name + " " + (env.ci.pullRequest ? log.color.red("[triggered by pull request]") : "")
      : log.color.red("Not running in CI environment.")
  );

  logger.info(log.color.gray("Git HEAD:") + " %s", env.git.head);

  logger.info(
    log.color.gray("Git branch:") + " %s %s",
    env.git.branch || log.color.dim("none"),
    !matchBranch(options.branch, env.git.branch) ? log.color.red("[not configured for release]") : ""
  );

  logger.info(log.color.gray("NPM registry:") + " %s", env.npm.registryUrl);

  let count = 0;
  for (const pkg of env.packages.values()) {
    if (pkg.canRelease) count++;
  }

  logger.info("Releasing %s of %s packages.", count, env.packages.size);
  logger.info();
}

interface DisplayPackageOptions {
  expanded?: boolean;
  showAll?: boolean;
  printChangelog?: boolean;
}

function displayPackage(logger: PrefixedLogger, pkg: Package, env: Env, options?: DisplayPackageOptions) {
  options = options || {};
  if (!options.showAll && !pkg.canRelease) return;

  logger.info(
    pkg.toString({
      format: options.expanded ? "expanded" : "basic",
      color: log.color.level > 0,
      env,
    })
  );

  if (options.printChangelog) {
    logger.info(
      pkg.toString({
        format: "changelog_md",
        env,
      })
    );
  }
}

function displayProblem(logger: PrefixedLogger, type: "error" | "warning", problem: string, pkgs?: Set<string>) {
  const color = type === "error" ? "red" : "yellow";
  const logtype = type === "error" ? "ERR!" : "WARN";
  logger.info("  " + log.color[color](logtype) + " " + problem);
  if (pkgs)
    pkgs.forEach((p) => {
      logger.info("    + %s", p);
    });
}

function displayProblems(
  logger: PrefixedLogger,
  warnings: Map<string, Set<string>>,
  errors: Map<string, Set<string>>,
  displayPackages?: boolean
) {
  if (!warnings.size && !errors.size) return;

  logger.info(log.color.bold("Problems:"));

  for (const [warning, pkgs] of warnings) {
    displayProblem(logger, "warning", warning, displayPackages ? pkgs : undefined);
  }

  for (const [error, pkgs] of errors) {
    displayProblem(logger, "error", error, displayPackages ? pkgs : undefined);
  }

  logger.info();

  if (errors.size) {
    throw new Error("There was an issue with this release. Please correct it to continue.");
  }
}
